import React, { useState } from 'react';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Plus, Trash2, ChevronUp, ChevronDown, Link2, X } from 'lucide-react';
import { ReportEvidenceEditor } from './ReportEvidenceEditor';

let _uid = 0;
const newId = () => `local-${Date.now()}-${_uid++}`;

// ── Development Summary ──────────────────────────────────────────────────────
export const SummaryEditor = ({ value, onChange, onCommit }) => (
  <div data-testid="summary-editor">
    <Textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onCommit}
      rows={7}
      placeholder="Write a concise professional overview of the athlete's development across this review period…"
      className="resize-y"
      data-testid="summary-textarea"
    />
    <p className="mt-1 text-xs text-slate-400">Saved automatically when you click away.</p>
  </div>
);

// ── Evidence picker (references only assembled/authorized evidence) ───────────
const EvidencePicker = ({ available, selected, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const selIds = new Set((selected || []).map((e) => e.source_id));
  const toggle = (item) => {
    if (selIds.has(item.source_id)) {
      onChange((selected || []).filter((e) => e.source_id !== item.source_id));
    } else {
      onChange([...(selected || []), item]);
    }
  };
  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1.5">
        {(selected || []).map((e) => (
          <span key={e.source_id} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
            <Link2 className="h-3 w-3" />{e.label}
            <button type="button" onClick={() => toggle(e)} className="text-slate-400 hover:text-rose-600"><X className="h-3 w-3" /></button>
          </span>
        ))}
        <button type="button" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-2 py-0.5 text-[11px] text-slate-500 hover:bg-slate-50" data-testid={`${testId}-add`}>
          <Plus className="h-3 w-3" /> Link evidence
        </button>
      </div>
      {open && (
        <div className="mt-2 max-h-52 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2">
          {(available || []).length === 0 && <div className="px-2 py-1 text-xs text-slate-400">No assembled evidence available yet.</div>}
          {(available || []).map((item) => (
            <button
              key={item.source_id}
              type="button"
              onClick={() => toggle(item)}
              className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-slate-50 ${selIds.has(item.source_id) ? 'text-emerald-700' : 'text-slate-600'}`}
            >
              <input type="checkbox" readOnly checked={selIds.has(item.source_id)} className="pointer-events-none" />
              <span className="truncate">{item.label}</span>
              <span className="ml-auto shrink-0 text-[10px] uppercase text-slate-400">{item.source_type}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Key Strengths (2–4 recommended) ──────────────────────────────────────────
export const StrengthsEditor = ({ items, setItems, onCommit, availableEvidence }) => {
  const update = (id, patch) => setItems(items.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const add = () => { const next = [...items, { id: newId(), title: '', description: '', evidence_refs: [], order: items.length }]; setItems(next); onCommit(next); };
  const remove = (id) => { const next = items.filter((s) => s.id !== id).map((s, i) => ({ ...s, order: i })); setItems(next); onCommit(next); };
  const move = (id, dir) => {
    const idx = items.findIndex((s) => s.id === id);
    const j = idx + dir; if (j < 0 || j >= items.length) return;
    const next = [...items]; [next[idx], next[j]] = [next[j], next[idx]];
    const reordered = next.map((s, i) => ({ ...s, order: i })); setItems(reordered); onCommit(reordered);
  };
  return (
    <div className="space-y-3" data-testid="strengths-editor">
      {items.length === 0 && <p className="text-sm text-slate-400">No strengths yet. Recommended: 2–4.</p>}
      {items.map((s, i) => (
        <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`strength-${i}`}>
          <div className="flex items-start gap-2">
            <div className="flex flex-col pt-1">
              <button type="button" onClick={() => move(s.id, -1)} className="text-slate-300 hover:text-slate-600" data-testid={`strength-up-${i}`}><ChevronUp className="h-4 w-4" /></button>
              <button type="button" onClick={() => move(s.id, 1)} className="text-slate-300 hover:text-slate-600" data-testid={`strength-down-${i}`}><ChevronDown className="h-4 w-4" /></button>
            </div>
            <div className="flex-1 space-y-2">
              <Input value={s.title} onChange={(e) => update(s.id, { title: e.target.value })} onBlur={() => onCommit(items)} placeholder="Strength title (e.g. Composure in possession)" data-testid={`strength-title-${i}`} />
              <Textarea value={s.description || ''} onChange={(e) => update(s.id, { description: e.target.value })} onBlur={() => onCommit(items)} rows={2} placeholder="Description / evidence context" data-testid={`strength-desc-${i}`} />
              <EvidencePicker available={availableEvidence} selected={s.evidence_refs} onChange={(refs) => { update(s.id, { evidence_refs: refs }); onCommit(items.map((x) => x.id === s.id ? { ...x, evidence_refs: refs } : x)); }} testId={`strength-evidence-${i}`} />
            </div>
            <button type="button" onClick={() => remove(s.id)} className="text-slate-300 hover:text-rose-600" data-testid={`strength-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} data-testid="strength-add"><Plus className="mr-1 h-4 w-4" /> Add Strength</Button>
    </div>
  );
};

// ── Priority Development Areas (3–5 recommended) ─────────────────────────────
export const PrioritiesEditor = ({ items, setItems, onCommit, availableEvidence, reportId }) => {
  const [openId, setOpenId] = useState(null);
  const update = (id, patch) => setItems(items.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const add = () => { const id = newId(); const next = [...items, { id, title: '', observation: '', development_objective: '', recommended_action: '', success_measure: '', review_date: '', evidence_refs: [], order: items.length }]; setItems(next); onCommit(next); setOpenId(id); };
  const remove = (id) => { const next = items.filter((p) => p.id !== id).map((p, i) => ({ ...p, order: i })); setItems(next); onCommit(next); };
  const move = (id, dir) => {
    const idx = items.findIndex((p) => p.id === id);
    const j = idx + dir; if (j < 0 || j >= items.length) return;
    const next = [...items]; [next[idx], next[j]] = [next[j], next[idx]];
    const reordered = next.map((p, i) => ({ ...p, order: i })); setItems(reordered); onCommit(reordered);
  };
  return (
    <div className="space-y-3" data-testid="priorities-editor">
      {items.length === 0 && <p className="text-sm text-slate-400">No priorities yet. Recommended: 3–5.</p>}
      {items.map((p, i) => {
        const open = openId === p.id;
        return (
          <div key={p.id} className="rounded-xl border border-slate-200 bg-white" data-testid={`priority-${i}`}>
            <div className="flex items-center gap-2 px-3 py-2.5">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-800 text-xs font-semibold text-white">{i + 1}</span>
              <button type="button" className="flex-1 text-left text-sm font-medium text-slate-800 truncate" onClick={() => setOpenId(open ? null : p.id)} data-testid={`priority-toggle-${i}`}>
                {p.title || <span className="text-slate-400">Untitled priority — click to edit</span>}
              </button>
              <button type="button" onClick={() => move(p.id, -1)} className="text-slate-300 hover:text-slate-600" data-testid={`priority-up-${i}`}><ChevronUp className="h-4 w-4" /></button>
              <button type="button" onClick={() => move(p.id, 1)} className="text-slate-300 hover:text-slate-600" data-testid={`priority-down-${i}`}><ChevronDown className="h-4 w-4" /></button>
              <button type="button" onClick={() => remove(p.id)} className="text-slate-300 hover:text-rose-600" data-testid={`priority-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
            {open && (
              <div className="space-y-2 border-t border-slate-100 px-3 py-3">
                <Input value={p.title} onChange={(e) => update(p.id, { title: e.target.value })} onBlur={() => onCommit(items)} placeholder="Priority title (e.g. Scanning before receiving)" data-testid={`priority-title-${i}`} />
                <Field label="Observation — what staff has observed"><Textarea value={p.observation || ''} onChange={(e) => update(p.id, { observation: e.target.value })} onBlur={() => onCommit(items)} rows={2} data-testid={`priority-observation-${i}`} /></Field>
                <Field label="What needs improvement"><Textarea value={p.needs_improvement || ''} onChange={(e) => update(p.id, { needs_improvement: e.target.value })} onBlur={() => onCommit(items)} rows={2} data-testid={`priority-needs-${i}`} /></Field>
                <Field label="Why it matters"><Textarea value={p.why_matters || ''} onChange={(e) => update(p.id, { why_matters: e.target.value })} onBlur={() => onCommit(items)} rows={2} data-testid={`priority-why-${i}`} /></Field>
                <Field label="Expected standard"><Textarea value={p.expected_standard || ''} onChange={(e) => update(p.id, { expected_standard: e.target.value })} onBlur={() => onCommit(items)} rows={2} data-testid={`priority-standard-${i}`} /></Field>
                <Field label="Development Objective — what needs to improve"><Textarea value={p.development_objective || ''} onChange={(e) => update(p.id, { development_objective: e.target.value })} onBlur={() => onCommit(items)} rows={2} data-testid={`priority-objective-${i}`} /></Field>
                <Field label="Player Action — what the player will work on"><Textarea value={p.player_action || ''} onChange={(e) => update(p.id, { player_action: e.target.value })} onBlur={() => onCommit(items)} rows={2} data-testid={`priority-player-action-${i}`} /></Field>
                <Field label="PFAP / Coach Action — what staff will do"><Textarea value={p.pfap_action || ''} onChange={(e) => update(p.id, { pfap_action: e.target.value })} onBlur={() => onCommit(items)} rows={2} data-testid={`priority-pfap-action-${i}`} /></Field>
                <Field label="Success Measure — how progress will be recognised"><Textarea value={p.success_measure || ''} onChange={(e) => update(p.id, { success_measure: e.target.value })} onBlur={() => onCommit(items)} rows={2} data-testid={`priority-success-${i}`} /></Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Field label="Review Date (optional)"><Input type="date" value={(p.review_date || '').slice(0, 10)} onChange={(e) => update(p.id, { review_date: e.target.value })} onBlur={() => onCommit(items)} data-testid={`priority-review-${i}`} /></Field>
                  <Field label="Supporting Evidence"><EvidencePicker available={availableEvidence} selected={p.evidence_refs} onChange={(refs) => { update(p.id, { evidence_refs: refs }); onCommit(items.map((x) => x.id === p.id ? { ...x, evidence_refs: refs } : x)); }} testId={`priority-evidence-${i}`} /></Field>
                </div>
                <ReportEvidenceEditor items={p.evidence || []} reportId={reportId} onChange={(next) => { const merged = items.map((x) => x.id === p.id ? { ...x, evidence: next } : x); setItems(merged); onCommit(merged); }} testId={`priority-report-evidence-${i}`} />
              </div>
            )}
          </div>
        );
      })}
      <Button variant="outline" size="sm" onClick={add} data-testid="priority-add"><Plus className="mr-1 h-4 w-4" /> Add Priority</Button>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-slate-500">{label}</label>
    {children}
  </div>
);
